"use client";

import { withManageTripsNextClientHeaders } from "@jb-manage-trips/api/manage-trips-client-headers";
import type { HealthCheckResponse } from "@jb-manage-trips/types/pnr";

export const API_MOCK = process.env.NEXT_PUBLIC_USE_DEV_MOCKS === "true";

type JsonFetchInit = {
  body?: unknown;
  credentials?: RequestCredentials;
  headers?: Record<string, string>;
  method?: "GET" | "POST";
  signal?: AbortSignal;
};

type HttpErrorLike = Error & {
  code?: string;
  errorCode?: string;
  httpStatus?: number;
  response?: unknown;
  status?: number;
  statusCode?: number;
};

/** Minimal JSON fetch wrapper used by both services */
export async function jsonFetch<T>(
  url: string,
  init: JsonFetchInit = {},
): Promise<T> {
  const baseHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const headers = withManageTripsNextClientHeaders(
    init.headers ? { ...baseHeaders, ...init.headers } : baseHeaders,
  );

  const response = await fetch(url, {
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    credentials: init.credentials ?? "include",
    headers,
    method: init.method ?? "POST",
    signal: init.signal,
  });

  const text = await response.text();
  const parsed = text ? safeParse(text) : null;

  if (!response.ok) {
    const error: HttpErrorLike = Object.assign(
      new Error(`HTTP ${String(response.status)}`),
      {
        code: `JSON_FETCH_HTTP_${String(response.status)}`,
        errorCode: `JSON_FETCH_HTTP_${String(response.status)}`,
        httpStatus: response.status,
        response: parsed,
        status: response.status,
        statusCode: response.status,
      },
    );
    throw error;
  }

  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** Central health check used by both flows */
export async function healthCheck(
  url: string,
  payload: { b6PNRRecordLocator: string; lastName: string },
  signal?: AbortSignal,
): Promise<HealthCheckResponse> {
  if (API_MOCK) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    return {
      b6PNRRecordLocator: payload.b6PNRRecordLocator,
      emailExist: true,
      phoneNumberExist: true,
      pnrState: "normal",
    };
  }
  return jsonFetch<HealthCheckResponse>(url, { body: payload, signal });
}
/** Shared DX URL builder */
export function buildSabreDxUrlShared(
  baseUrl: string,
  lastName: string,
  pnrRecordLocator: string,
  queryParameters?: Record<string, string | undefined>,
  os?: "iOS" | "Android" | "Other",
): string {
  const locale = queryParameters?.locale;
  const platform = queryParameters?.un_jtt_application_platform;

  let source: string | undefined;
  if (platform) {
    if (os === "iOS") {
      source = "imobile";
    } else if (os === "Android") {
      source = "amobile";
    }
  }

  const q = new URLSearchParams({
    lastName,
    pnr: pnrRecordLocator,
    ...(locale ? { locale } : {}),
    ...(source ? { source } : {}),
  });
  return `${baseUrl}?${q.toString()}`;
}
/** Single place for the qualifier-gating rule */
export function shouldRedirectToQualifier(options: {
  emailExist?: boolean;
  phoneNumberExist?: boolean;
  skipQualifierPhoneMissing: boolean;
}): boolean {
  const { emailExist, phoneNumberExist, skipQualifierPhoneMissing } = options;
  if (skipQualifierPhoneMissing) {
    return !emailExist;
  }
  return !emailExist || !phoneNumberExist;
}
